import {useAuth} from '@apps-common/auth-react';
import {LIGHT_THEME as planetTheme} from '@apps-common/ui-theme/Themes';
import {Box, hexToRgb, useColorScheme} from '@mui/material';
import PLMap from '@planet/maps/Map.js';
import View from '@planet/maps/View';
import DrawInteraction from '@planet/maps/interaction/Draw';
import MapboxLayer from '@planet/maps/layer/MapboxVector.js';
import TileLayer from '@planet/maps/layer/Tile';
import VectorLayer from '@planet/maps/layer/Vector';
import VectorSource from '@planet/maps/source/Vector';
import XYZSource from '@planet/maps/source/XYZ';
import debounce from 'lodash/debounce';
import {Attribution} from 'ol/control.js';
import GeoJsonFormat from 'ol/format/GeoJSON.js';
import {fromLonLat} from 'ol/proj.js';
import TileUTFGrid from 'ol/source/UTFGrid';
import Icon from 'ol/style/Icon';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {connect, useSelector} from 'react-redux';
import {
  setByCoordinate as setGeometryByCoordinate,
  setByExtent as setGeometryByExtent,
} from '../actions/geometry.js';
import {setExtent, setView as setMapView} from '../actions/map.js';
import {load as loadSelectedScene} from '../actions/selected-scene.js';
import setProvenanceData from '../actions/set-provenance-data.js';
import {drawModes, modes, setDrawMode} from '../actions/ui.js';
import {DRAG_TYPE} from '../common.js';
import {
  BASE_LAYER_STYLES,
  LABEL_STYLES,
  MAPBOX_ACCESS_TOKEN,
} from '../config.js';
import {getActiveQuad, getSelectedQuad} from '../selectors/index.js';
import geometryFromExtent from '../util/geometry-from-extent.js';
import {addQueryParams} from '../util/url.js';
import CompareLabel from './compare-label.jsx';
import DropTarget from './drop-target.jsx';
import {DRAW_INTERACTIONS} from './map-interactions.js';

const format = new GeoJsonFormat();

const iconColor = hexToRgb(planetTheme.palette.primary.main);
const locationSvg =
  '<svg width="22px" height="32px" viewBox="0 0 22 32" version="1.1" xmlns="http://www.w3.org/2000/svg">' +
  `<path d="M11,0 C4.91857143,0 0,5.00799991 0,11.1999998 C0,19.5999997 11,31.9999995 11,31.9999995 C11,31.9999995 22,19.5999997 22,11.1999998 C22,5.00799991 17.0814286,0 11,0 Z" fill="${iconColor}"></path>` +
  '<path d="M11,15.1999997 C8.792,15.1999997 7,13.4079998 7,11.1999998 C7,8.99199985 8.792,7.19999988 11,7.19999988 C13.208,7.19999988 15,8.99199985 15,11.1999998 C15,13.4079998 13.208,15.1999997 11,15.1999997 Z" fill="white"></path>' +
  '</svg>';

const nullStyle = new Style({});
const pointStyle = new Style({
  image: new Icon({
    anchor: [0.5, 0],
    anchorOrigin: 'bottom-left',
    src: `data:image/svg+xml;utf8,${locationSvg}`,
  }),
});

const getLineStringStyle = color => {
  return new Style({
    stroke: new Stroke({
      color,
      width: 2,
      lineDash: [5, 10],
    }),
  });
};

const getStrokeStyle = (color, dash) => {
  return new Style({
    stroke: new Stroke({
      color,
      lineDash: dash ? [4, 8] : null,
      width: 2,
    }),
  });
};

const getColorForProperties = properties => {
  if (properties.footprint) {
    return planetTheme.palette.primary.main;
  } else if (properties.active) {
    return planetTheme.palette.primary.main;
  } else if (properties.selected) {
    return planetTheme.palette.primary.main;
  }
  return planetTheme.palette.grey[100];
};

const getStyleForGeometry = (geometry, properties) => {
  const color = getColorForProperties(properties);
  switch (geometry.getType()) {
    case 'GeometryCollection':
      return geometry
        .getGeometries()
        .map(subgeometry => getStyleForGeometry(subgeometry, properties));
    case 'Point':
      // TODO: change nullStyle to plain ol' null after issues get resolved
      // context here: https://hello.planet.com/code/apps/mosaics/merge_requests/37#note_48291
      return properties.active ? pointStyle : nullStyle;
    case 'LineString':
      return getLineStringStyle(color);
    case 'Polygon':
      return properties.footprint
        ? getStrokeStyle(color, true)
        : getStrokeStyle(color);
    default:
      return null;
  }
};

const getLayerStyle = feature => {
  return getStyleForGeometry(feature.getGeometry(), feature.getProperties());
};

const augmentGeometry = (geometry, extent) => {
  switch (geometry.type) {
    case 'Point': {
      const crosshairs = geometry.coordinates.map((coord, index) => ({
        type: 'LineString',
        coordinates: index
          ? [
              [extent[0], coord],
              [extent[2], coord],
            ]
          : [
              [coord, extent[1]],
              [coord, extent[3]],
            ],
      }));

      return {
        type: 'GeometryCollection',
        geometries: [geometry, ...crosshairs],
      };
    }
    default:
      return geometry;
  }
};

const ANIMATION_DURATION = 300;

const MosaicMap = ({
  loadSelectedScene,
  setDrawMode,
  setGeometryByExtent,
  setMapView,
  setGeometryByCoordinate,
  setProvenanceData,
  mosaic,
  mapExtent,
  setExtent,
  provenanceData,
  center,
  zoom,
  sx,
  ...props
}) => {
  const auth = useAuth();

  const {mode: themeMode} = useColorScheme();

  const uiMode = useSelector(state => state.ui.mode);
  const drawMode = useSelector(state => state.ui.drawMode);

  const uiModeRef = useRef(uiMode);
  const drawModeRef = useRef(drawMode);
  const attributionRef = useRef();
  const utfLayerRef = useRef();
  const minProvenanceZoom = useRef();
  const zoomTimeout = useRef();

  const [dragging, setDragging] = useState(false);
  const [utfVisible, setUtfVisible] = useState(false);
  const [viewState, setViewState] = useState({center: [0, 0], zoom: 2});
  const [currentExtent, setCurrentExtent] = useState();
  const [viewRef, setViewRef] = useState();

  const viewRefChanged = useCallback(node => {
    if (node !== null) {
      setViewRef(node);
    }
  }, []);

  useEffect(() => {
    setViewState({center, zoom});
  }, [center, zoom]);

  const onViewChange = useCallback(
    event => {
      const view = event.target;
      const newCenter = view.getCenter();
      const newZoom = view.getZoom();
      const extent = view.calculateExtent();
      setCurrentExtent(extent);
      setMapView({
        center: newCenter,
        zoom: newZoom,
      });
    },
    [setMapView],
  );

  useEffect(() => {
    uiModeRef.current = uiMode;
  }, [uiMode]);

  useEffect(() => {
    drawModeRef.current = drawMode;
  }, [drawMode]);

  const url = useMemo(
    () =>
      mosaic
        ? addQueryParams(mosaic._links.tiles, {
            token: auth.user?.accessToken,
          })
        : '',
    [auth, mosaic],
  );

  const onMapSingleClick = useCallback(
    event => {
      const updateProvenanceData = coordinate => {
        if (!coordinate || !utfLayerRef.current) {
          return;
        }
        const mapView = event.map.getView();
        const resolution = mapView.getResolution();
        const utfSource = utfLayerRef.current?.getSource();
        utfSource?.forDataAtCoordinateAndResolution(
          fromLonLat(coordinate),
          resolution,
          debounce(data => {
            if (!data?.includes('unknown')) {
              setProvenanceData(data);
            }
          }, 300),
          true,
        );
      };
      if (
        uiModeRef.current === modes.SELECTION &&
        drawModeRef.current === drawModes.POINT
      ) {
        setGeometryByCoordinate(event.coordinate);
        updateProvenanceData(event.coordinate);
      }
    },
    [setProvenanceData, setGeometryByCoordinate],
  );

  const onDragEnter = event => {
    const types = [...event.dataTransfer.types];
    if (types.includes(DRAG_TYPE)) {
      setDragging(true);
    }
  };

  const onDragLeave = event => {
    const types = [...event.dataTransfer.types];
    if (types.includes(DRAG_TYPE)) {
      setDragging(false);
    }
  };

  const onDragOver = event => {
    event.preventDefault();
  };

  const onDrop = event => {
    if (dragging) {
      setDragging(false);

      const mosaic = event.dataTransfer.getData(DRAG_TYPE);
      props.onDrop(mosaic);
    }
  };

  const zoomToExtent = useCallback(
    extent => {
      setUtfVisible(false);
      viewRef?.fit(extent, {
        duration: ANIMATION_DURATION,
        padding: [40, 40, 40, 40],
      });
    },
    [viewRef],
  );

  const onDrawEnd = useCallback(
    event => {
      const extent = event.feature.getGeometry().getExtent();

      setGeometryByExtent(extent);
      setDrawMode(drawModes.POINT);
    },
    [setDrawMode, setGeometryByExtent],
  );

  const pointPin = useMemo(() => {
    if (props.geometry && currentExtent) {
      const feature = {
        type: 'Feature',
        geometry: augmentGeometry(props.geometry, currentExtent),
        properties: {
          active: !props.activeQuad,
        },
      };

      return format.readFeatures({
        type: 'FeatureCollection',
        features: [feature],
      });
    }
    return [];
  }, [currentExtent, props.activeQuad, props.geometry]);

  const selectedQuadFeature = useMemo(() => {
    if (props.selectedQuad) {
      const feature = {
        type: 'Feature',
        geometry: geometryFromExtent(props.selectedQuad.bbox),
        properties: {
          selected: true,
        },
      };
      return format.readFeatures({
        type: 'FeatureCollection',
        features: [feature],
      });
    }
    return [];
  }, [props.selectedQuad]);

  const activeQuadFeature = useMemo(() => {
    if (props.activeQuad) {
      const feature = {
        type: 'Feature',
        geometry: geometryFromExtent(props.activeQuad.bbox),
        properties: {
          selected: true,
        },
      };
      return format.readFeatures({
        type: 'FeatureCollection',
        features: [feature],
      });
    }
    return [];
  }, [props.activeQuad]);

  const footprintFeature = useMemo(() => {
    if (props.footprint) {
      const feature = {
        type: 'Feature',
        geometry: props.footprint,
        properties: {
          footprint: true,
        },
      };
      return format.readFeatures({
        type: 'FeatureCollection',
        features: [feature],
      });
    }
    return [];
  }, [props.footprint]);

  const onProvenanceSourceChange = event => {
    const tileGrid = event.target.getTileGrid();
    if (tileGrid) {
      minProvenanceZoom.current = tileGrid.getMinZoom();
    }
  };

  useEffect(() => {
    if (mosaic && utfLayerRef.current) {
      const source = new TileUTFGrid({
        url: `https://api.planet.com/basemaps/v1/mosaics/${mosaic?.id}/tiles?token=${auth.user?.accessToken}`,
        preemptive: false,
      });
      source.on('change', onProvenanceSourceChange);
      utfLayerRef.current.setSource(source);
      source.refresh();
    }
  }, [auth.user?.accessToken, mosaic]);

  useEffect(() => {
    if (provenanceData) {
      loadSelectedScene();
    }
  }, [loadSelectedScene, provenanceData]);

  useEffect(() => {
    if (mapExtent) {
      setExtent();
      zoomToExtent(mapExtent);
    }
  }, [setExtent, mapExtent, zoomToExtent]);

  useEffect(() => {
    if (minProvenanceZoom.current !== null) {
      // work around an open layers UTF bug by delaying visibility of the
      // provenance layer until the map has completed the animated zoom
      // without this, an expensive loop may lock the browser
      // see https://hello.planet.com/jira/browse/WEBAPPS-845?focusedCommentId=581218
      clearTimeout(zoomTimeout.current);
      const utfVisible = zoom >= minProvenanceZoom.current;
      zoomTimeout.current = setTimeout(() => {
        setUtfVisible(utfVisible);
      }, ANIMATION_DURATION);
    }
  }, [zoom]);

  useEffect(() => {
    if (viewRef) {
      setCurrentExtent(viewRef.calculateExtent());
    }
  }, [viewRef]);

  return (
    <Box
      onDragEnter={onDragEnter}
      data-qe="mosaic-map"
      sx={theme => ({
        flexGrow: 1,
        userSelect: 'none',
        position: 'relative',
        ...(typeof sx === 'function' ? sx(theme) : sx),
      })}
    >
      <PLMap
        controls={[
          new Attribution({
            target: attributionRef.current,
            collapsible: false,
          }),
        ]}
        onSingleClick={onMapSingleClick}
        style={{
          width: '100%',
          height: '100%',
          cursor: uiMode === modes.SELECTION ? 'crosshair' : 'default',
        }}
      >
        <View
          ref={viewRefChanged}
          center={viewState.center}
          zoom={viewState.zoom}
          onChange={onViewChange}
        />

        <DrawInteraction
          active={drawMode === drawModes.AREA}
          options={DRAW_INTERACTIONS[drawModes.AREA]}
          onDrawEnd={onDrawEnd}
        />

        <TileLayer ref={utfLayerRef} visible={utfVisible} />

        <MapboxLayer
          options={{
            accessToken: MAPBOX_ACCESS_TOKEN,
            styleUrl: BASE_LAYER_STYLES[themeMode ?? 'dark'],
          }}
          onSourceReady={event => {
            event.target
              .getSource()
              .setAttributions([
                '©<a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a>',
                '©<a href="http://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
              ]);
          }}
        />

        <TileLayer visible={!!mosaic}>
          <XYZSource
            url={url}
            options={{
              crossOrigin: 'anonymous',
            }}
          />
        </TileLayer>

        <MapboxLayer
          visible={props.labelsVisible}
          options={{
            accessToken: MAPBOX_ACCESS_TOKEN,
            styleUrl: LABEL_STYLES[themeMode ?? 'dark'],
          }}
        />

        <VectorLayer visible={!!props.geometry} style={getLayerStyle}>
          <VectorSource features={pointPin} />
        </VectorLayer>

        <VectorLayer visible={!!props.selectedQuad} style={getLayerStyle}>
          <VectorSource features={selectedQuadFeature} />
        </VectorLayer>

        <VectorLayer visible={!!props.activeQuad} style={getLayerStyle}>
          <VectorSource features={activeQuadFeature} />
        </VectorLayer>

        <VectorLayer visible={!!props.footprint} style={getLayerStyle}>
          <VectorSource features={footprintFeature} />
        </VectorLayer>
      </PLMap>

      {props.withLabel && mosaic && <CompareLabel>{mosaic.title}</CompareLabel>}

      <Box ref={attributionRef} />

      <DropTarget
        open={dragging}
        style={{position: 'absolute'}}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
      />
    </Box>
  );
};

MosaicMap.propTypes = {
  activeQuad: PropTypes.object,
  comparing: PropTypes.bool.isRequired,
  footprint: PropTypes.object,
  labelsVisible: PropTypes.bool.isRequired,
  mosaic: PropTypes.object,
  onDrop: PropTypes.func.isRequired,
  geometry: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  withLabel: PropTypes.bool,
  provenanceData: PropTypes.string,
  selectedQuad: PropTypes.object,
  loadSelectedScene: PropTypes.func,
  setProvenanceData: PropTypes.func,
  setGeometryByCoordinate: PropTypes.func,
  setGeometryByExtent: PropTypes.func,
  setDrawMode: PropTypes.func,
  setMapView: PropTypes.func,
  mapExtent: PropTypes.array,
  setExtent: PropTypes.func,
  center: PropTypes.array,
  zoom: PropTypes.number,
};

const mapStateToProps = state => ({
  selectedQuad: getSelectedQuad(state),
  activeQuad: getActiveQuad(state),
  comparing: state.ui.mode === modes.COMPARE,
  footprint: state.footprint,
  geometry: state.geometry.json,
  labelsVisible: state.ui.labels,
  center: state.map.center,
  zoom: state.map.zoom,
  mapExtent: state.map.extent,
  provenanceData: state.provenanceData,
});

const mapDispatchProps = {
  loadSelectedScene,
  setProvenanceData,
  setGeometryByCoordinate,
  setGeometryByExtent,
  setDrawMode,
  setMapView,
  setExtent,
};

export default connect(mapStateToProps, mapDispatchProps)(MosaicMap);
